import _ from "lodash"
import React, { Fragment, useRef } from "react"
import { AirportSeasonConfigurations } from "../../api/AirportSeasonConfiguration"
import { Transaction } from "../../api/Transaction"

import Button, { btnBlurEvent } from "../generic/buttons/Button"
import TransactionFlight from "./TransactionFlight"
import { Flight } from "../../api/Flight"
import {
  ActionType,
  EditMode,
  FlightMode,
  StatusCode,
  handleGetFlightChanges,
  PageGroup
} from "../../common/transaction"
import { getActionTypeText, getFlightModeText } from "../Utils"
import TransactionFlightTurnaround from "./TransactionFlightTurnaround"
import useClickOutside from "../../hooks/useClickOutsite"
import { CodeType } from "../AirportSelector"

import { FieldName } from "../../common/text"
import { useAppSelector } from "../../app/hooks"
import { CustomShowLabelConfig } from "./TransactionFlightHeaders"

export interface ImainTransactions {
  editMode: EditMode
  pageGroup: PageGroup
  airportSeasons: AirportSeasonConfigurations
  codeType: CodeType
  actionType?: ActionType
  inFocus?: boolean
  placement?: string
  touched?: boolean
  dateHasError?: boolean
  dateHasErrText?: string
  customShowLabelConfig: CustomShowLabelConfig
}
interface Props extends ImainTransactions {
  transaction: Transaction
  transactionItemIDs?: string[]
  isInitResponse?: boolean
  initialTransaction?: Transaction
  onChangeInFocus?: (isInFocus: boolean) => void
  onChange?: (
    flight: Transaction,
    error?: boolean,
    field?: string,
    val?: any
  ) => void
  onDelete?: (e?: any) => void

  onCheckAvailability?: () => void
  onAutofill?: () => void
  disableAutoFillBtn?: boolean
  showTimeRangeError?: boolean
  placement?: string
  onFillDown?: (flightIdx: number, field: FieldName) => void
  isEditSeries?: boolean
}

const TransactionItem: React.FunctionComponent<Props> = ({
  transaction,
  airportSeasons,
  editMode,
  pageGroup,
  actionType,
  codeType,
  inFocus,
  isInitResponse,
  transactionItemIDs,
  initialTransaction,
  onChange,
  onDelete,
  onChangeInFocus,
  onCheckAvailability,
  placement,
  touched,
  showTimeRangeError,
  disableAutoFillBtn,
  onAutofill,
  onFillDown,
  dateHasError,
  dateHasErrText,
  customShowLabelConfig,
  isEditSeries
}) => {
  const { useLocalTime } =
    useAppSelector(
      (state) => state["selectionReducer"].airportSeasonsSelector
    ) || {}
  const flightModes = _.uniq(transaction.flights.map((f) => f.flightMode))
  const isHandling = pageGroup === PageGroup.HANDLING
  const showAvailability = isHandling // TODO: switch off availability button here
  const hasMultiFlightModes =
    flightModes.length > 1 ||
    actionType === ActionType.MERGE ||
    actionType === ActionType.DELINK ||
    actionType === ActionType.LINK
  const itemRef = useRef()

  const handleFlightChange = (
    updFlight: Flight,
    flightIdx: number,
    error?: boolean,
    field?: string,
    val?: any
  ) => {
    const updTransaction = _.cloneDeep(transaction)
    updTransaction.flights[flightIdx] = updFlight

    onChange(updTransaction, error, field, val)
  }

  const handleFlightsChange = (
    updFlights: Flight[],
    error?: boolean,
    field?: string,
    val?: any
  ) => {
    const updTransaction = _.cloneDeep(transaction)
    updTransaction.flights = updFlights

    onChange(updTransaction, error, field, val)
  }

  const handleBlur = (e: btnBlurEvent) => {
    if (onChangeInFocus && !e.relatedTarget) {
      onChangeInFocus(false)
    }
  }

  useClickOutside({
    ref: itemRef,
    onClickOutside: () => {
      if (inFocus && onChangeInFocus) {
        onChangeInFocus(false)
      }
    },
    insideElementsIds: transactionItemIDs.concat([
      "transItem-" + transaction.transId + "-start",
      "transItem-" + transaction.transId + "-end",
      "ocsDatePickerPortal",
      "sendAllBtn"
    ]),
    disableScrollBarClick: true
  })

  const flightChanges = handleGetFlightChanges(
    transaction,
    initialTransaction,
    actionType
  )

  const editFlightRows =
    !hasMultiFlightModes &&
    flightModes[0] === FlightMode.Turnaround &&
    (actionType === ActionType.CHANGE ||
      actionType === ActionType.SEARCH ||
      actionType === ActionType.CLEARREQ) ? (
      <TransactionFlightTurnaround
        transId={transaction.transId}
        flights={transaction.flights}
        airports={[]}
        isEditSeries={isEditSeries}
        pageGroup={pageGroup}
        gcrMode={transaction.gcrMode}
        airportSeasons={airportSeasons}
        inFocus={inFocus}
        codeType={codeType}
        flightChanges={flightChanges}
        onChange={handleFlightsChange}
        onFillDown={onFillDown}
        touched={touched}
        customShowLabelConfig={customShowLabelConfig}
      />
    ) : (
      transaction.flights.map((flight, idx) => {
        return (
          <TransactionFlight
            key={idx}
            idx={idx}
            transId={transaction.transId}
            flight={flight}
            grcMode={transaction.gcrMode}
            airportSeasons={airportSeasons}
            editMode={editMode}
            pageGroup={pageGroup}
            isEditSeries={isEditSeries}
            actionType={actionType}
            inFocus={inFocus}
            codeType={codeType}
            onChange={(flight, error: boolean, name: string, val?: any) =>
              handleFlightChange(flight, idx, error, name, val)
            }
            touched={touched}
            hasMultiFlightModes={hasMultiFlightModes}
            fieldsWithChanges={flightChanges[idx]}
            isInitResponse={isInitResponse}
            isLastFlight={idx + 1 === transaction.flights.length}
            placement={placement}
            showTimeRangeError={showTimeRangeError}
            onFillDown={(field) => onFillDown(idx, field)}
            dateHasError={dateHasError}
            dateHasErrText={dateHasErrText}
            customShowLabelConfig={customShowLabelConfig}
          />
        )
      })
    )

  const statusCode = transaction.flights[0].statusCode
  const hasChanges =
    actionType === ActionType.CHANGE ||
    actionType === ActionType.SHLCHANGECONTINUATION ||
    actionType === ActionType.SHLCHANGENOOFFER
      ? flightChanges.filter((fc) => fc.length).length > 0
      : true

  const containsResponseCodeT = transaction.flights.find(
    (flight) => flight.responseCode === "T"
  )

  return (
    <div
      id={"transItem" + transaction.transId}
      className={
        "d-flex ocs-transaction-item-container" +
        (useLocalTime ? " ocs-transaction-item-local-time" : "") +
        (editMode === EditMode.ADD ? " ocs-transaction-item-add" : "") +
        (editMode === EditMode.EDIT ? " ocs-transaction-item-edit" : "") +
        (editMode === EditMode.RESPONSE
          ? " ocs-transaction-item-response"
          : "") +
        (statusCode === StatusCode.Offered || containsResponseCodeT
          ? " code-warning"
          : "") +
        (statusCode === StatusCode.Refused ? " code-danger" : "") +
        (inFocus ? " in-focus" : "") +
        (editMode === EditMode.EDIT && hasChanges ? " has-changes" : "")
      }
      onClick={() => {
        onChangeInFocus && onChangeInFocus(true)
      }}
      onFocus={(e) => {
        const target = e.target as HTMLElement
        if (!inFocus && onChangeInFocus && target.id !== "deleteBtn") {
          onChangeInFocus(true)
        }
      }}
      ref={itemRef}
    >
      {actionType && (
        <div className="ocs-transaction-item-actiontype">
          <p
            style={{
              whiteSpace:
                actionType === ActionType.SWAPPERIOD ? "unset" : "nowrap"
            }}
          >
            {getActionTypeText(actionType)}
          </p>
        </div>
      )}
      {!hasMultiFlightModes && (
        <div className="d-flex align-items-center ocs-transaction-item-flightmode">
          <p>{getFlightModeText(flightModes[0])}</p>
        </div>
      )}
      {!hasMultiFlightModes && editMode !== EditMode.ADD && (
        <span className="ocs-transaction-item-divider code-divider">
          <div />
        </span>
      )}
      <div className="d-flex flex-column justify-content-center ocs-transaction-item-flights-container">
        {editFlightRows}
      </div>

      {editMode !== EditMode.RESPONSE && (
        <Fragment>
          <span className="ocs-transaction-item-divider">
            <div />
          </span>
          <div
            className={`ocs-transaction-item-btns d-flex flex-column justify-content-center ${
              editMode === EditMode.ADD && !showAvailability
                ? "flight-availability-hidden"
                : ""
            }`}
          >
            {onCheckAvailability && showAvailability && (
              <Button onClick={onCheckAvailability} type="button">
                Availability
              </Button>
            )}
            <div
              className={
                "d-flex ocs-transaction-item-btns-container " +
                (onCheckAvailability ? "ocs-mt-8" : "")
              }
            >
              {onDelete && editMode === EditMode.ADD ? (
                <div id="2">
                  <Button
                    idName="deleteBtn"
                    dataField={transaction.transId.toString()}
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete(e)
                    }}
                    icon="delete_outline"
                    className="ocs-mr-8 mq-btn-height"
                    onBlur={handleBlur}
                    type="button"
                    tabIndex={-1}
                  />
                </div>
              ) : (
                <Button
                  idName="deleteBtn"
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete()
                  }}
                  type="button"
                  onBlur={handleBlur}
                  tabIndex={-1}
                  className="mq-btn-height"
                >
                  Deselect
                </Button>
              )}
              {onAutofill && (
                <div>
                  <Button
                    type="button"
                    className="mq-btn-height"
                    onClick={(e) => {
                      e.stopPropagation()
                      onAutofill()
                    }}
                    onBlur={handleBlur}
                    tabIndex={-1}
                    disabled={disableAutoFillBtn}
                  >
                    Fill
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Fragment>
      )}
    </div>
  )
}

export default TransactionItem

TransactionItem.defaultProps = {
  transactionItemIDs: []
}
